/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Dismissable Alert
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       9th April 2025

*******************************************************************************************/
import React                    from 'react';
import { MainAlert }            from 'components';

// --- Constants ---
const DEFAULT_DURATION          = 24 * 60 * 60 * 1000;          // 24 Hours
const DEFAULT_CHECK_FREQUENCY   = 60 * 1000;                    // 60 Seconds
const DEFAULT_ID                = "dismissibleAlert";           // ID
const DEFAULT_STORAGE_TYPE      = "local";                      // Storage Type

/**
 * Retrieves the appropriate storage based on type.
 * 
 * @param {'local'|'session'} [type=DEFAULT_STORAGE_TYPE]
 * @returns {Storage} The chosen storage object.
 */
const getStorage = (type = DEFAULT_STORAGE_TYPE) => {
    return type === 'session' ? sessionStorage : localStorage;
};

/**
 * Checks if an alert has been dismissed based on its unique identifier and the specified duration.
 * 
 * @param {string} id - Unique identifier for the alert.
 * @param {'local'|'session'} [storageType=DEFAULT_STORAGE_TYPE] - Type of storage to use.
 * @param {number} [duration=DEFAULT_DURATION] - Duration (in ms) to consider the alert dismissed.
 * @returns {boolean}
 */
const isDismissed = (
    id, 
    storageType = DEFAULT_STORAGE_TYPE,
    duration    = DEFAULT_DURATION
) => {
    try {
        const storage = getStorage(storageType);
        const stored = storage.getItem(id);
        if (!stored) 
            return false;
        const timestamp = JSON.parse(stored);
        return Date.now() - timestamp < duration;
    } catch (e) {
        console.warn('Failed to read dismissal from storage', e);
    }
    return false;
};

/**
 * Marks the alert as dismissed by storing the current timestamp.
 * 
 * @param {string} id - Unique identifier for the alert.
 * @param {'local'|'session'} [storageType=DEFAULT_STORAGE_TYPE] - Type of storage to use.
 * @returns {boolean}
 */
const dismissAlert = (
    id, 
    storageType = DEFAULT_STORAGE_TYPE
) => {
    try {
        const storage = getStorage(storageType);
        const now = Date.now();
        storage.setItem(id, JSON.stringify(now));
        return true;
    } catch (e) {
        console.warn('Failed to write dismissal to storage', e);
    }
    return false;
};

/**
 * A component that displays a dismissible alert with configurable dismissal behavior.
 * The alert will remain visible until it is dismissed, and its dismissal state is 
 * stored in either `localStorage` or `sessionStorage` for a specified duration. 
 * The component will periodically check the dismissal state and update its visibility.
 *
 * @component
 *
 * @example
 * // Example usage of AlertSessionDismissible
 * <AlertSessionDismissible 
 *   id="customAlert" 
 *   duration={604800000}        // 7 days
 *   checkFrequency={5000}       // check every 5 seconds
 *   storageType="session"       // use sessionStorage instead of localStorage
 * >
 *   <strong>Warning:</strong> This is a dismissible alert.
 * </AlertSessionDismissible>
 *
 * @param {Object} props - The component props.
 * @param {string} [props.id=DEFAULT_ID] - The unique identifier for the alert, used to store the dismissal state.
 * @param {number} [props.duration=DEFAULT_DURATION] - Duration (in milliseconds) for which the alert should stay dismissed.
 * @param {number} [props.checkFrequency=DEFAULT_CHECK_FREQUENCY] - How often (in milliseconds) to check if the alert should be re-shown.
 * @param {'local'|'session'} [props.storageType=DEFAULT_STORAGE_TYPE] - The storage type to use for dismissal state: `local` or `session`.
 * @param {React.ReactNode} props.children - The content to be rendered inside the alert.
 * @param {Object} props.rest - Additional props forwarded to the underlying MUI `Alert` component.
 *
 * @returns {JSX.Element|null} The rendered alert component, or `null` if dismissed within the specified duration.
 */
export const AlertSessionDismissible = ({ 
    id                                  = DEFAULT_ID, 
    storageType                         = DEFAULT_STORAGE_TYPE,
    duration       : durationIn         = DEFAULT_DURATION, 
    checkFrequency : checkFrequencyIn   = DEFAULT_CHECK_FREQUENCY,
    children, 
    ...props
}) => {
    const [open, setOpen]       = React.useState(false);
    const idKey                 = React.useMemo(() => `${id}Dismissed`, [id]);
    const duration              = React.useMemo(() => Number.isFinite(durationIn) ? durationIn : 0, [durationIn]);
    const checkFrequency        = React.useMemo(() => Number.isFinite(checkFrequencyIn) ? checkFrequencyIn : 0, [checkFrequencyIn]);
    const handleOpen            = React.useCallback(() => setOpen(true), []);
    const handleClose           = React.useCallback(() => { dismissAlert(idKey, storageType); setOpen(false); }, [idKey, storageType]);
    const intervalRef           = React.useRef(null);

    React.useEffect(() => {

        // Update Open State
        const updateOpen = () => {
            setOpen(
                duration > 0
                    ? !isDismissed(idKey, storageType, duration)
                    : true
            );
        };

        // Clear any existing interval
        clearInterval(intervalRef.current); 

        // Check once immediately
        updateOpen(); 

        // Set up interval if freq > 0
        if(checkFrequency > 0 && duration > 0){

            // Set the interval
            intervalRef.current = setInterval(updateOpen, checkFrequency ); 

            // Clean up the interval on unmount
            return () => {
                clearInterval(intervalRef.current); 
            };
        }

    }, [idKey, duration, checkFrequency, storageType]);

    return (
        <MainAlert
            severity    = "info"
            dismissable = {true}
            {...props}
            id          = {id}
            open        = {open}
            onClose     = {handleClose}
            onOpen      = {handleOpen}
        >
            {children}
        </MainAlert>
    );
};

export default AlertSessionDismissible;